import React, { useState } from 'react';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../ui/input-otp';
import { challengeAndVerifyMfa, friendlyAuthError, signOutSupabase } from '../../lib/supabase';

/**
 * Shared TOTP challenge UI for login and OAuth callback.
 * Parent supplies factorId; on success parent continues (checkAuth / navigate).
 */
export default function MfaChallengeForm({
  factorId,
  onVerified,
  onCancel,
  title = 'Two-factor authentication',
  subtitle = 'Enter the 6-digit code from your authenticator app.',
}) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (value) => {
    const digits = String(value || code || '').trim();
    if (digits.length !== 6 || !factorId) return;
    setLoading(true);
    setError('');
    try {
      const { error: verifyErr } = await challengeAndVerifyMfa({ factorId, code: digits });
      if (verifyErr) throw verifyErr;
      await onVerified?.();
    } catch (err) {
      setError(friendlyAuthError(err) || 'Invalid code. Try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setError('');
    if (onCancel) {
      await onCancel();
      return;
    }
    await signOutSupabase();
  };

  return (
    <div className="w-full max-w-md mx-auto" data-testid="mfa-challenge">
      <h2 className="text-xl font-bold text-[#2C2825] mb-2 font-heading">{title}</h2>
      <p className="text-sm text-[#8F8984] mb-6">{subtitle}</p>
      {error && (
        <p className="text-sm text-[#D94545] bg-[#FDECEC] p-3 rounded-xl mb-4" data-testid="mfa-challenge-error">
          {error}
        </p>
      )}
      <div className="flex justify-center mb-6" data-testid="mfa-otp-input">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={(v) => setCode(v)}
          onComplete={(v) => submit(v)}
          disabled={loading}
        >
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      <button
        type="button"
        onClick={() => submit(code)}
        disabled={loading || code.length !== 6}
        className="w-full bg-[#1A1B1E] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#2C2825] transition-colors disabled:opacity-50"
        data-testid="mfa-verify"
      >
        {loading ? 'Verifying…' : 'Verify'}
      </button>
      <button
        type="button"
        onClick={handleCancel}
        disabled={loading}
        className="w-full mt-3 text-sm font-semibold text-[#8F8984] hover:text-[#2C2825]"
        data-testid="mfa-cancel"
      >
        Cancel and sign out
      </button>
    </div>
  );
}
